import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumericInput } from '@/components/NumericInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';

const ShowerDetailDialog = ({ open, onOpenChange, shower, onSave, showerGlassTypes = [] }) => {
  const [formData, setFormData] = useState({
    showerWidth_in: null, showerHeight_in: null,
    roWidth_in: null, roHeight_in: null, lites: []
  });

  useEffect(() => {
    if (shower && open) {
      const lites = shower.lites && shower.lites.length > 0
        ? shower.lites.map((lite, i) => ({ id: i + 1, width_in: lite.width_in || null, height_in: lite.height_in || null, glassTypeId: lite.glassTypeId || '' }))
        : [];
      setFormData({
        showerWidth_in: shower.showerWidth_in || null,
        showerHeight_in: shower.showerHeight_in || null,
        roWidth_in: shower.roWidth_in || null,
        roHeight_in: shower.roHeight_in || null,
        lites
      });
    }
  }, [shower, open]);

  const handleAddLite = () => {
    const newId = formData.lites.length > 0 ? Math.max(...formData.lites.map(l => l.id)) + 1 : 1;
    setFormData({ ...formData, lites: [...formData.lites, { id: newId, width_in: null, height_in: null, glassTypeId: '' }] });
  };

  const handleRemoveLite = (id) => {
    setFormData({ ...formData, lites: formData.lites.filter(l => l.id !== id) });
  };

  const handleLiteChange = (id, field, value) => {
    setFormData({ ...formData, lites: formData.lites.map(l => l.id === id ? { ...l, [field]: value } : l) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Shower Details: {shower?.label}</DialogTitle>
          <DialogDescription>Enter dimensions and glass lite specifications</DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="space-y-6 mt-4">
          {/* Shower Dimensions */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg border-b pb-2">Shower Dimensions</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Shower Width (in)</Label>
                <NumericInput allowFraction value={formData.showerWidth_in || ''} onChange={(v) => setFormData({ ...formData, showerWidth_in: v })} placeholder="e.g., 48" />
              </div>
              <div className="space-y-2">
                <Label>Shower Height (in)</Label>
                <NumericInput allowFraction value={formData.showerHeight_in || ''} onChange={(v) => setFormData({ ...formData, showerHeight_in: v })} placeholder="e.g., 80" />
              </div>
              <div className="space-y-2">
                <Label>Rough Opening Width (in)</Label>
                <NumericInput allowFraction value={formData.roWidth_in || ''} onChange={(v) => setFormData({ ...formData, roWidth_in: v })} placeholder="e.g., 50" />
              </div>
              <div className="space-y-2">
                <Label>Rough Opening Height (in)</Label>
                <NumericInput allowFraction value={formData.roHeight_in || ''} onChange={(v) => setFormData({ ...formData, roHeight_in: v })} placeholder="e.g., 82" />
              </div>
            </div>
          </div>

          {/* Glass Lites */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <div>
                <h3 className="font-semibold text-lg">Glass Lites</h3>
                <p className="text-xs text-slate-500 mt-1">Add glass lites for this shower</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={handleAddLite} className="flex items-center gap-1">
                <Plus className="h-4 w-4" /> Add Glass Lite
              </Button>
            </div>

            {formData.lites.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <div className="grid grid-cols-[80px_1fr_1fr_1fr_40px] gap-2 bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 uppercase">
                  <div></div>
                  <div>Width (in)</div>
                  <div>Height (in)</div>
                  <div>Glass Type</div>
                  <div></div>
                </div>
                <div className="divide-y divide-slate-200">
                  {formData.lites.map((lite, index) => (
                    <div key={lite.id} className="grid grid-cols-[80px_1fr_1fr_1fr_40px] gap-2 px-3 py-2 items-center bg-white hover:bg-slate-50">
                      <div className="text-sm font-medium text-slate-700">Lite {index + 1}</div>
                      <div>
                        <NumericInput allowFraction value={lite.width_in || ''} onChange={(v) => handleLiteChange(lite.id, 'width_in', v)} placeholder="0" className="h-8 text-sm" />
                      </div>
                      <div>
                        <NumericInput allowFraction value={lite.height_in || ''} onChange={(v) => handleLiteChange(lite.id, 'height_in', v)} placeholder="0" className="h-8 text-sm" />
                      </div>
                      <div>
                        <Select value={lite.glassTypeId || 'none'} onValueChange={(val) => handleLiteChange(lite.id, 'glassTypeId', val === 'none' ? '' : val)}>
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue placeholder="Select Glass Type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {showerGlassTypes.map(gt => (
                              <SelectItem key={gt.id} value={gt.id}>{gt.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-center">
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveLite(lite.id)} className="h-8 w-8 p-0 hover:bg-red-500 hover:text-white transition-colors">
                          <Trash2 className="h-4 w-4 text-red-500 hover:text-white" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 border rounded-lg bg-slate-50">
                <p className="text-slate-500 text-sm">No glass lites added yet</p>
                <Button type="button" variant="link" size="sm" onClick={handleAddLite} className="mt-2">
                  <Plus className="h-4 w-4 mr-1" /> Add first glass lite
                </Button>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-[#FF6600] hover:bg-[#ff8533]">Save</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ShowerDetailDialog;
